import React, { useState } from 'react'

/**
 * 자막과 제목이 적용된 MP4를 다운로드하는 버튼
 * Canvas 미리보기와 동일한 자막과 제목을 서버에서 적용
 */
function SubtitleDownloadButton({ section, subtitleSettings, titleSettings, videoSettings }) {
  const [isGenerating, setIsGenerating] = useState(false)
  
  // section prop이 변경될 때마다 로그 출력 (비활성화됨)
  React.useEffect(() => {
    // 로그 출력 비활성화
  }, [section])
  
  const handleDownloadWithSubtitle = async () => {
    // console.log('🚀 다운로드 시작')
    // console.log('📋 section 객체:', section)
    // console.log(`📍 Section ID: ${section.id}`)
    // console.log(`🎵 Section Audio URL: ${section.audioFile}`)
    // console.log('📺 videoSettings:', videoSettings)
    // console.log('🎨 subtitleSettings:', subtitleSettings)
    // console.log('📝 titleSettings:', titleSettings)
    
    // 🎯 이미 자막이 포함된 MP4를 그대로 다운로드 (서버에서 이미 drawtext 자막 적용됨)
    // Canvas는 미리보기용이고, 실제 MP4에는 이미 자막이 있음
    
    // 미디어 파일이 없는 경우
    if (!section.imageFile && !section.mp4File) {
      // console.log('❌ 미디어 파일이 없습니다')
      // console.log('section.imageFile:', section.imageFile)
      // console.log('section.mp4File:', section.mp4File)
      return
    }
    
    // console.log('✅ 미디어 파일 확인됨')
    
    // 자막 텍스트가 없거나 빈 문자열인 경우 - 이미지만 있으면 MP4로 변환
    if (!section.text || section.text.trim() === '') {
      // console.log('⚠️ 자막 텍스트가 없습니다')
      
      // MP4가 있으면 원본 다운로드
      if (section.mp4File) {
        // console.log('📹 MP4 원본 다운로드')
        const link = document.createElement('a')
        link.href = section.mp4File
        link.download = `${section.imageFile?.name.split('.')[0] || 'output'}.mp4`
        link.click()
        return
      }
      
      // 이미지만 있으면 서버에서 MP4로 변환
      // console.log('🖼️ 이미지를 MP4로 변환하여 다운로드')
      // 자막 없이 계속 진행
    }
    
    // console.log('✅ 미리보기와 동일하게 다운로드 준비')
    
    setIsGenerating(true)
    
    try {
      // 서버에서 자막 적용
      const formData = new FormData()
      
      // 원본 파일 타입에 따라 처리
      // 중요: 원본 파일(imageFile 또는 videoFile)이 있으면 우선 사용
      // 이렇게 해야 서버에서 위치 설정을 제대로 적용할 수 있음
      // console.log('🔍 파일 타입 확인 중...')
      // console.log('  - imageFile:', section.imageFile)
      // console.log('  - videoFile:', section.videoFile)
      // console.log('  - mp4File:', section.mp4File)
      
      if (section.imageFile) {
        // 이미지 또는 GIF 파일 (원본 우선)
        // console.log('🖼️ 원본 이미지 사용:', section.imageFile.name)
        
        formData.append('file', section.imageFile, section.imageFile.name)
        formData.append('is_original_image', 'true')  // 원본 이미지/GIF임을 표시
        
        // 비디오 길이 계산 (오디오가 있으면 오디오 길이, 없으면 기본 5초)
        const videoDuration = section.audioDuration || 5.0
        formData.append('video_duration', videoDuration.toString())
      } else if (section.videoFile) {
        // 원본 비디오 파일이 있는 경우
        // console.log('🎥 원본 비디오 사용:', section.videoFile.name)
        
        formData.append('file', section.videoFile, section.videoFile.name)
        formData.append('is_original_image', 'false')  // 비디오 파일임을 명시
      } else if (section.mp4File) {
        // MP4 파일만 있는 경우 (이미 생성된 MP4)
        // console.log('🎬 생성된 MP4 파일 사용:', section.mp4File)
        
        // MP4가 Blob URL이면 fetch로 가져오기
        if (section.mp4File.startsWith('blob:')) {
          const response = await fetch(section.mp4File)
          const blob = await response.blob()
          // console.log('📹 비디오 Blob 크기:', blob.size, 'bytes')
          formData.append('file', blob, 'video.mp4')
        } else {
          // 서버에서 생성된 MP4 URL
          const response = await fetch(section.mp4File)
          const blob = await response.blob()
          formData.append('file', blob, 'video.mp4')
        }
        
        // MP4 파일임을 명시
        formData.append('is_original_image', 'false')
      } else {
        console.error('❌ 미디어 파일이 없습니다')
        return
      }
      
      // 자막 텍스트 추가 (없어도 빈 문자열로 전송)
      formData.append('subtitle_text', section.text || '')
      
      // 음성 파일 추가 (있는 경우에만)
      if (section.audioFile) {
        // console.log(`🎵 Section ${section.id} 음성 파일 추가:`, section.audioFile)
        // console.log(`📍 Section 전체 정보:`, {
        //   id: section.id,
        //   text: section.text?.substring(0, 30) + '...',
        //   audioFile: section.audioFile
        // })
        
        // 모든 섹션의 audioFile 확인 (디버깅용)
        const allSections = document.querySelectorAll('[data-section-id]')
        // console.log('🔍 현재 페이지의 모든 섹션 오디오 상태:')
        allSections.forEach(el => {
          const sectionId = el.getAttribute('data-section-id')
          // console.log(`  - Section ${sectionId}: audioFile 존재 여부 확인 필요`)
        })
        
        // 음성이 Blob URL이면 fetch로 가져오기
        if (section.audioFile.startsWith('blob:')) {
          const audioResponse = await fetch(section.audioFile)
          const audioBlob = await audioResponse.blob()
          // console.log(`🎵 Section ${section.id} Blob 크기:`, audioBlob.size, 'bytes')
          
          // 오디오 데이터의 처음 100바이트 해시 생성 (고유성 확인용)
          const arrayBuffer = await audioBlob.slice(0, 100).arrayBuffer()
          const hashArray = Array.from(new Uint8Array(arrayBuffer))
          const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
          // console.log(`🔑 Section ${section.id} 오디오 시그니처 (처음 100바이트):`, hashHex.substring(0, 40) + '...')
          
          // 디버깅용: 오디오를 output 폴더에 저장
          const debugAudioName = `audio_section_${section.id}_${Date.now()}.mp3`
          formData.append('audio_file', audioBlob, debugAudioName)
          formData.append('debug_audio_name', debugAudioName) // 서버에서 디버깅용 파일명
        } else {
          // 서버에서 생성된 오디오 URL
          const audioResponse = await fetch(section.audioFile)
          const audioBlob = await audioResponse.blob()
          // console.log(`🎵 Section ${section.id} 서버 오디오 Blob 크기:`, audioBlob.size, 'bytes')
          
          const debugAudioName = `audio_section_${section.id}_${Date.now()}.mp3`
          formData.append('audio_file', audioBlob, debugAudioName)
          formData.append('debug_audio_name', debugAudioName)
        }
      }
      
      // 자막 설정 (미리보기와 동일하게)
      // console.log('🎨 자막 설정:', subtitleSettings)
      formData.append('subtitle_settings', JSON.stringify(subtitleSettings))
      
      // 비디오 위치 설정 추가
      // videoSettings가 없거나 비어있을 때 기본값 사용
      const videoSettingsToSend = videoSettings || { videoYOffset: 0, fillTo916: false }
      // console.log('📺 비디오 위치 설정 전송할 값:', {
      //   raw: videoSettings,
      //   toSend: videoSettingsToSend,
      //   json: JSON.stringify(videoSettingsToSend)
      // })
      formData.append('video_settings', JSON.stringify(videoSettingsToSend))
      
      // 제목 설정 추가 (자막 설정과 동일한 패턴)
      // console.log('📝 제목 설정 확인:', {
      //   titleSettings,
      //   enabled: titleSettings?.enabled,
      //   text: titleSettings?.text,
      //   willSend: titleSettings && titleSettings.enabled && titleSettings.text
      // })
      
      if (titleSettings && titleSettings.enabled && titleSettings.text) {
        // console.log('✅ 제목 설정 전송:', JSON.stringify(titleSettings))
        formData.append('title_settings', JSON.stringify(titleSettings))
      } else {
        // console.log('⚠️ 제목 설정 전송 안함 - 조건 불충족')
      }
      
      // 서버에 자막 추가 요청
      const result = await fetch(`${window.location.origin}/api/video/add-subtitle`, {
        method: 'POST',
        body: formData
      })
      
      if (!result.ok) {
        const errorText = await result.text()
        console.error('❌ 서버 응답 에러:', {
          status: result.status,
          statusText: result.statusText,
          body: errorText
        })
        throw new Error(`자막 적용 실패: ${result.status} ${result.statusText}`)
      }
      
      // 자막이 적용된 비디오 다운로드
      // console.log('✅ 서버 응답 성공, 다운로드 시작...')
      const videoBlob = await result.blob()
      // console.log('📦 Blob 크기:', videoBlob.size, 'bytes')
      
      const url = URL.createObjectURL(videoBlob)
      // console.log('🔗 다운로드 URL 생성:', url)
      
      const link = document.createElement('a')
      link.href = url
      const filename = `${section.imageFile?.name.split('.')[0] || 'output'}_subtitle.mp4`
      link.download = filename
      // console.log('💾 다운로드 파일명:', filename)
      
      document.body.appendChild(link)  // DOM에 추가
      link.click()
      document.body.removeChild(link)  // 클릭 후 제거
      // console.log('✅ 다운로드 트리거 완료')
      
      // URL 정리
      setTimeout(() => URL.revokeObjectURL(url), 1000)
      
    } catch (error) {
      console.error('자막 적용 다운로드 실패:', error)
      alert('자막 적용 중 오류가 발생했습니다. 원본을 다운로드합니다.')
      
      // 실패 시 원본 다운로드
      const link = document.createElement('a')
      link.href = section.mp4File
      link.download = `${section.imageFile?.name.split('.')[0] || 'output'}.mp4`
      link.click()
    } finally {
      setIsGenerating(false)
    }
  }
  
  return (
    <button
      onClick={handleDownloadWithSubtitle}
      disabled={isGenerating}
      className="download-button-top"
      title={isGenerating ? '자막 적용 중...' : 'MP4 다운로드 (자막 포함)'}
      style={{
        opacity: isGenerating ? 0.5 : 1,
        cursor: isGenerating ? 'wait' : 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff' // 아이콘 색상을 글자색으로 제어
      }}
      aria-label={isGenerating ? '자막 적용 중' : 'MP4 다운로드'}
    >
      {isGenerating ? (
        // 로딩
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 330 330"
          fill="rgb(255 255 255 / 67%)"
        >
          <path d="M97.5,165c0-8.284-6.716-15-15-15h-60c-8.284,0-15,6.716-15,15s6.716,15,15,15h60
            C90.784,180,97.5,173.284,97.5,165z"/>
          <path d="M307.5,150h-30c-8.284,0-15,6.716-15,15s6.716,15,15,15h30c8.284,0,15-6.716,15-15S315.784,150,307.5,150z"/>
          <path d="M172.5,90c8.284,0,15-6.716,15-15V15c0-8.284-6.716-15-15-15s-15,6.716-15,15v60
            C157.5,83.284,164.216,90,172.5,90z"/>
          <path d="M172.501,240c-8.284,0-15,6.716-15,15v60c0,8.284,6.716,15,15,15c8.284,0,15-6.716,15-15v-60
            C187.501,246.716,180.785,240,172.501,240z"/>
          <path d="M77.04,48.327c-5.856-5.858-15.354-5.857-21.213,0c-5.858,5.858-5.858,15.355,0,21.213l42.427,42.428
            c2.929,2.929,6.768,4.394,10.606,4.394c3.838,0,7.678-1.465,10.606-4.393c5.858-5.858,5.858-15.355,0-21.213L77.04,48.327z"/>
          <path d="M246.746,218.034c-5.857-5.857-15.355-5.857-21.213,0c-5.858,5.858-5.857,15.355,0,21.213l42.428,42.426
            c2.929,2.929,6.768,4.393,10.607,4.393c3.839,0,7.678-1.465,10.606-4.393c5.858-5.858,5.858-15.355,0-21.213L246.746,218.034z"/>
          <path d="M98.254,218.034L55.828,260.46c-5.858,5.858-5.858,15.355,0,21.213c2.929,2.929,6.768,4.393,10.607,4.393
            c3.839,0,7.678-1.464,10.606-4.393l42.426-42.426c5.858-5.858,5.858-15.355,0-21.213
            C113.609,212.176,104.111,212.176,98.254,218.034z"/>
        </svg>
      ) : (
        // 다운로드 아이콘
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="rgb(255 255 255 / 67%)"
          style={{ display: 'block' }}
        >
          <path d="m21 14a1 1 0 0 0 -1 1v4a1 1 0 0 1 -1 1h-14a1 1 0 0 1 -1-1v-4a1 1 0 0 0 -2 0v4a3 3 0 0 0 3 3h14a3 3 0 0 0 3-3v-4a1 1 0 0 0 -1-1zm-9.71 1.71a1 1 0 0 0 .33.21.94.94 0 0 0 .76 0 1 1 0 0 0 .33-.21l4-4a1 1 0 0 0 -1.42-1.42l-2.29 2.3v-9.59a1 1 0 0 0 -2 0v9.59l-2.29-2.3a1 1 0 1 0 -1.42 1.42z"/>
        </svg>
      )}
    </button>
  )
}

export default SubtitleDownloadButton